import React from 'react';
import { useToggle } from 'react-use';
import TextInput from 'application/components/form-controls/text-input';
import Icon from 'application/components/icon';
import { ValidatedFieldProps } from 'application/hooks/use-validated-form';
import useTranslate from 'application/hooks/use-translate';

enum PasswordStrength {
  WEAK = 'WEAK',
  MEDIUM = 'MEDIUM',
  STRONG = 'STRONG'
}

interface Props extends ValidatedFieldProps {
  value: string;
  disabled?: boolean;
  required?: boolean;
  validateStrength?: boolean;
  onChange: (value: string) => void;
}

const evaluatePasswordStrength = (password: string): PasswordStrength => {
  if (password.length < 8) return PasswordStrength.WEAK;
  if (/[^A-Za-z0-9]/.test(password)) return PasswordStrength.STRONG;

  return PasswordStrength.MEDIUM;
};

const PasswordInput = ({ value, validateStrength = false, customValidation, ...textInputProps }: Props) => {
  const [passwordIsVisible, togglePasswordIsVisible] = useToggle(false);
  const translate = useTranslate('components.form-controls.password-input');

  const passwordStrength = validateStrength ? evaluatePasswordStrength(value) : null;

  return (
    <>
      <div className="relative w-full">
        <TextInput
          type={passwordIsVisible ? 'text' : 'password'}
          value={value}
          placeholder="***********"
          customValidation={
            passwordStrength && value
              ? {
                  isValid: passwordStrength !== PasswordStrength.WEAK,
                  errorMessage: translate('strength-error')
                }
              : customValidation
          }
          {...textInputProps}
        />

        <button
          type="button"
          tabIndex={-1}
          className="absolute right-4 top-0 flex h-[54px] items-center text-neutral-900"
          onClick={togglePasswordIsVisible}
        >
          <Icon icon={passwordIsVisible ? 'eye-slash' : 'eye'} />
        </button>
      </div>

      {passwordStrength && passwordStrength !== PasswordStrength.WEAK && (
        <div className="mt-1 text-sm" role="alert">
          {translate('password-strength-prefix')}
          {passwordStrength === PasswordStrength.MEDIUM && (
            <span className="font-bold text-yellow-500">{translate('strength-medium')}</span>
          )}
          {passwordStrength === PasswordStrength.STRONG && (
            <span className="font-bold text-green-500">{translate('strength-strong')}</span>
          )}
        </div>
      )}
    </>
  );
};

export default PasswordInput;
